<script setup>
  import { ref, computed, watch, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import axios from '../../services/ApiService.js';
  import DateRangePicker from '../../pages/catalog_publication_fields/Date.vue';
  import SearchableList from '../SearchableList.vue';
  import CategoryBrowserDialog from './CategoryBrowserDialog.vue';

  const { t } = useI18n();
  const i18nScope = ref('components.library_catalog');

  const props = defineProps({
    visibleColumns: {
      type: Object,
      required: true
    },
    searchQuery: {
      type: String,
      default: ''
    },
    selectedLanguage: {
      type: String,
      default: null
    },
    selectedFormat: {
      type: String,
      default: null
    },
    selectedCategory: {
      type: String,
      default: null
    },
    selectedAudience: {
      type: String,
      default: null
    },
    startDate: {
      type: String,
      default: null
    },
    endDate: {
      type: String,
      default: null
    },
    acquisitionStartDate: {
      type: String,
      default: null
    },
    acquisitionEndDate: {
      type: String,
      default: null
    },
    lang: {
      type: String,
      required: true
    },
    audiences: {
      type: Array,
      default: () => []
    },
    libraryId: {
      type: Number,
      default: null
    }
  });

  const emit = defineEmits([
    'update:visibleColumns',
    'update:searchQuery',
    'update:selectedLanguage',
    'update:selectedFormat',
    'update:selectedCategory',
    'update:selectedAudience',
    'update:startDate',
    'update:endDate',
    'update:acquisitionStartDate',
    'update:acquisitionEndDate',
    'filter-change',
    'search',
    'export',
    'export-offers'
  ]);

  const localSearchQuery = ref(props.searchQuery);
  const localLanguage = ref({ id: null, name: '' });
  const localFormat = ref(props.selectedFormat);
  const localCategory = ref({ id: null, name: '' });
  const localAudience = ref(props.selectedAudience);
  const dateComponent = ref(null);
  const acquisitionDateComponent = ref(null);
  const languagesOptions = ref([]);
  const languagesLoading = ref(false);
  const categoriesOptions = ref([]);
  const categoriesLoading = ref(false);

  const formats = computed(() => [
    { id: 'audiobook', name: t(`${i18nScope.value}.filters.format_audiobook`) },
    { id: 'pdf', name: t(`${i18nScope.value}.filters.format_pdf`) },
    { id: 'epub', name: t(`${i18nScope.value}.filters.format_epub`) },
    { id: 'epub_fixed_layout', name: t(`${i18nScope.value}.filters.format_epub_fixed_layout`) },
    { id: 'epub_reflowable', name: t(`${i18nScope.value}.filters.format_epub_reflowable`) }
  ]);

  const fetchLanguages = async () => {
    languagesLoading.value = true;
    try {
      const params = props.libraryId ? { library_id: props.libraryId } : {};
      const response = await axios.get('/api/library_catalog/languages.json', { params });
      languagesOptions.value = response.data;
    } catch (error) {
      console.error('Error fetching languages:', error);
      languagesOptions.value = [];
    } finally {
      languagesLoading.value = false;
    }
  };

  const categorySearchTimeout = ref(null);

  const searchCategories = (searchTerm) => {
    if (!searchTerm?.trim()) {
      categoriesOptions.value = [];
      return;
    }

    clearTimeout(categorySearchTimeout.value);
    categorySearchTimeout.value = setTimeout(async () => {
      categoriesLoading.value = true;
      try {
        const response = await axios.get('/api/library_catalog/categories.json', {
          params: { q: searchTerm, lang: props.lang }
        });
        categoriesOptions.value = response.data;
      } catch (error) {
        console.error('Error fetching categories:', error);
        categoriesOptions.value = [];
      } finally {
        categoriesLoading.value = false;
      }
    }, 700);
  };

  watch(() => props.libraryId, () => {
    fetchLanguages();
  });

  onMounted(() => {
    fetchLanguages();
    handleManualSearch();
  });

  const handleLanguageChange = (selected) => {
    localLanguage.value = selected;
    emit('update:selectedLanguage', selected?.id || null);
    emit('filter-change');
  };

  const handleCategoryChange = (selected) => {
    localCategory.value = selected;
    emit('update:selectedCategory', selected?.id || null);
    emit('filter-change');
  };

  const handleDateChange = (event) => {
    const inputId = event.target.id;
    const dataType = event.target.getAttribute('data-type');

    if (inputId === `${dataType}_date_start`) {
      emit('update:startDate', event.target.value);
      emit('filter-change');
    } else if (inputId === `${dataType}_date_end`) {
      emit('update:endDate', event.target.value);
      emit('filter-change');
    }
  };

  const handleAcquisitionDateChange = (event) => {
    const inputId = event.target.id;
    const dataType = event.target.getAttribute('data-type');

    if (inputId === `${dataType}_date_start`) {
      emit('update:acquisitionStartDate', event.target.value);
      emit('filter-change');
    } else if (inputId === `${dataType}_date_end`) {
      emit('update:acquisitionEndDate', event.target.value);
      emit('filter-change');
    }
  };

  const handleDateClear = (field, inputId) => {
    if (inputId === 'expiration_date_start') emit('update:startDate', null);
    if (inputId === 'expiration_date_end') emit('update:endDate', null);
    emit('filter-change');
  };

  const handleAcquisitionDateClear = (field, inputId) => {
    if (inputId === 'acquisition_date_start') emit('update:acquisitionStartDate', null);
    if (inputId === 'acquisition_date_end') emit('update:acquisitionEndDate', null);
    emit('filter-change');
  };

  watch(() => props.searchQuery, (newVal) => {
    localSearchQuery.value = newVal;
  });

  watch(() => props.selectedLanguage, (newVal) => {
    if (!newVal) {
      localLanguage.value = { id: null, name: '' };
    }
  });

  watch(() => props.selectedFormat, (newVal) => {
    localFormat.value = newVal;
  });

  watch(() => props.selectedCategory, (newVal) => {
    if (!newVal) {
      localCategory.value = { id: null, name: '' };
    }
  });

  watch(() => props.selectedAudience, (newVal) => {
    localAudience.value = newVal;
  });

  watch(dateComponent, (newVal) => {
    if (newVal && props.startDate) newVal.setStartDate(props.startDate);
    if (newVal && props.endDate) newVal.setEndDate(props.endDate);
  }, { immediate: true });

  watch(acquisitionDateComponent, (newVal) => {
    if (newVal && props.acquisitionStartDate) newVal.setStartDate(props.acquisitionStartDate);
    if (newVal && props.acquisitionEndDate) newVal.setEndDate(props.acquisitionEndDate);
  }, { immediate: true });

  const showCategoryBrowser = ref(false);

  const handleCategoryBrowserSelect = (selected) => {
    handleCategoryChange(selected);
    showCategoryBrowser.value = false;
  };

  const columnKeys = [
    { key: 'title', label: `${i18nScope.value}.table.title` },
    { key: 'isbn', label: `${i18nScope.value}.table.isbn` },
    { key: 'author', label: `${i18nScope.value}.table.author` },
    { key: 'totalLoans', label: `${i18nScope.value}.table.total_loans` },
    { key: 'publisher', label: `${i18nScope.value}.table.publisher` },
    { key: 'category', label: `${i18nScope.value}.table.category` },
    { key: 'format', label: `${i18nScope.value}.table.format` },
    { key: 'language', label: `${i18nScope.value}.table.language` },
    { key: 'audience', label: `${i18nScope.value}.table.audience` },
    { key: 'acquisitionDate', label: `${i18nScope.value}.table.acquisition_date` },
    { key: 'expirationDate', label: `${i18nScope.value}.table.expiration_date` },
    { key: 'licenses', label: `${i18nScope.value}.table.licenses` },
    { key: 'reservations', label: `${i18nScope.value}.table.reservations` }
  ];

  const toggleColumn = (key) => {
    emit('update:visibleColumns', { ...props.visibleColumns, [key]: !props.visibleColumns[key] });
  };

  const handleExport = () => {
    emit('export');
  };

  const handleExportOffers = () => {
    emit('export-offers');
  };

  const handleManualSearch = () => {
    emit('filter-change');
  };
</script>

<template>
  <div class="library-catalog-filters">
    <div class="row w-100">

      <!-- Acquisition Date Range Filter -->
      <div class="col-6">
        <label class="form-label">{{ t(`${i18nScope}.filters.acquisition_date_range`) }}</label>
        <DateRangePicker
          ref="acquisitionDateComponent"
          type="acquisition"
          :selectFutureDates="false"
          @dateChange="handleAcquisitionDateChange"
          @clearField="handleAcquisitionDateClear"
        />
      </div>

      <!-- Expiration Date Range Filter -->
      <div class="col-6">
        <label class="form-label">{{ t(`${i18nScope}.filters.date_range`) }}</label>
        <DateRangePicker
          ref="dateComponent"
          type="expiration"
          @dateChange="handleDateChange"
          @clearField="handleDateClear"
        />
      </div>

    </div>

    <div class="row w-100 my-4">

      <!-- Category Filter -->
      <div class="col-3">
        <label class="form-label">{{ t(`${i18nScope}.filters.category`) }}</label>
        <div class="d-flex gap-1">
          <SearchableList
            v-model="localCategory"
            :options="categoriesOptions"
            :loading="categoriesLoading"
            :placeholder="t(`${i18nScope}.filters.all_categories`)"
            aria-label="Category"
            :clear-button-label="t(`${i18nScope}.filters.clear_category`)"
            :serverSearch="true"
            @change="handleCategoryChange"
            @search="searchCategories"
          />
          <button
            type="button"
            class="btn btn-outline-secondary flex-shrink-0"
            @click="showCategoryBrowser = true"
            data-bs-toggle="tooltip" :data-bs-title="t(`${i18nScope}.filters.browse_categories`)" :aria-label="t(`${i18nScope}.filters.browse_categories`)"
          >
            <i class="bi bi-folder2-open"></i>
          </button>
        </div>
        <CategoryBrowserDialog
          :show="showCategoryBrowser"
          :lang="lang"
          @close="showCategoryBrowser = false"
          @select="handleCategoryBrowserSelect"
        />
      </div>

      <!-- Format Filter -->
      <div class="col-3">
        <label class="form-label">{{ t(`${i18nScope}.filters.format`) }}</label>
        <select
          class="form-select"
          v-model="localFormat"
          @change="() => { emit('update:selectedFormat', localFormat); emit('filter-change'); }"
        >
          <option :value="null">{{ t(`${i18nScope}.filters.all_formats`) }}</option>
          <option
            v-for="format in formats"
            :key="format.id"
            :value="format.id"
          >
            {{ format.name }}
          </option>
        </select>
      </div>

      <!-- Language Filter -->
      <div class="col-3">
        <label class="form-label">{{ t(`${i18nScope}.filters.language`) }}</label>
        <SearchableList
          v-model="localLanguage"
          :options="languagesOptions"
          :loading="languagesLoading"
          :placeholder="t(`${i18nScope}.filters.all_languages`)"
          aria-label="Language"
          :clear-button-label="t(`${i18nScope}.filters.clear_language`)"
          @change="handleLanguageChange"
        />
      </div>

      <!-- Audience Filter -->
      <div class="col-3">
        <label class="form-label">{{ t(`${i18nScope}.filters.audience`) }}</label>
        <select
          class="form-select"
          v-model="localAudience"
          @change="() => { emit('update:selectedAudience', localAudience); emit('filter-change'); }"
        >
          <option :value="null">{{ t(`${i18nScope}.filters.all_audiences`) }}</option>
          <option
            v-for="audience in audiences"
            :key="audience.id"
            :value="audience.id"
          >
            {{ audience.name }}
          </option>
        </select>
      </div>

    </div>

    <div class="row w-100 my-2">
      <div class="col-12 d-flex justify-content-between">
        <div>
          <button
            type="button"
            class="btn btn-success"
            @click="handleExport">
            <i class="bi bi-filetype-xls me-2"></i>
            {{ t(`${i18nScope}.filters.export_to_excel`) }}
          </button>
          <button
            type="button"
            class="btn btn-success ms-2"
            @click="handleExportOffers">
            <i class="bi bi-file-earmark-spreadsheet me-2"></i>
            {{ t(`${i18nScope}.filters.export_offers`) }}
          </button>
        </div>
        <button
          type="button"
          class="btn btn-primary"
          @click="handleManualSearch">
          <i class="bi bi-search me-2"></i>
          {{ t(`${i18nScope}.filters.search`) }}
        </button>
      </div>
    </div>

    <div class="row w-100 mt-4">
      <div class="col-12 d-flex align-items-center flex-wrap gap-2">
        <span class="columns-label">{{ t(`${i18nScope}.filters.columns_visibility`) }}:</span>
        <div
          v-for="col in columnKeys"
          :key="col.key"
          class="form-check form-switch mb-0"
        >
          <input
            type="checkbox"
            role="switch"
            :id="`col-toggle-${col.key}`"
            class="form-check-input"
            :checked="visibleColumns[col.key]"
            @change="toggleColumn(col.key)"
          />
          <label :for="`col-toggle-${col.key}`" class="form-check-label">{{ t(col.label) }}</label>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
  .library-catalog-filters {
    padding: 20px 0;
  }

  .button-offset {
    margin-top: 30px !important;
  }

  .form-label {
    font-weight: 500;
    margin-bottom: 0.5rem;
  }

  .columns-label {
    font-weight: 500;
    font-size: 0.9rem;
    white-space: nowrap;
  }

  :deep(.form-switch .form-check-input:checked) {
    background-color: var(--primary);
    border-color: var(--primary);
    --bs-form-switch-bg: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23fff'/%3e%3c/svg%3e");
  }
</style>
